<template>
  <v-bottom-sheet v-model="showToast" :hide-overlay="true" :persistent="persistent || null">
    <v-sheet class="text-center container" :class="{success, error, info, warning}">
      <div class="wrapper">
        <i
          v-if="persistent && (ctaList.length>0)"
          class="material-icons close"
          @click="onClose()"
        >close</i>
        <div
          v-if="!custom"
          class="content"
          :class="{
            'align-left': persistent || ctaButton || ctaLink, 
            persistent
          }"
        >
          <div class="toast-simple">
            <i v-if="success && icon" class="material-icons toast-icon">check_circle</i>
            <i v-if="warning && icon" class="material-icons toast-icon">warning</i>
            <i v-if="error && icon" class="material-icons toast-icon">warning</i>
            <i v-if="info && icon" class="material-icons toast-icon">error</i>
            {{message}}
          </div>
          <div v-if="(ctaLink || ctaButton) && (ctaList.length > 0)" class="cta">
            <a v-if="ctaLink" class="cta-link" @click="ctaClickHandler">{{ctaLink}}</a>
            <button
              v-if="ctaButton"
              @click="ctaClickHandler"
              class="primary-solid short"
            >{{ctaButton}}</button>
          </div>
          <div v-if="ctaList.length > 0" class="cta list">
            <a @click="onCtaListOptionSelect(ctaList[0].id)">{{ctaList[0].text}}</a>
            <i
              ref="chevron"
              class="material-icons chevron-down"
              @click="showCtaList = true"
            >expand_more</i>
            <v-bottom-sheet v-model="showCtaList" inset persistent hide-overlay max-width="500">
              <v-sheet class="text-center cta-list-container" max-height="90vh">
                <div class="header">
                  <h3>Select Action</h3>
                  <i class="material-icons close" @click="onCtaListClose()">close</i>
                  <p>Success message text placeholder example here.</p>
                </div>
                <div class="actions">
                  <a
                    v-for="(cta, index) in ctaList"
                    :key="index"
                    @click="onCtaListOptionSelect(cta.id, true)"
                  >{{cta.text}}</a>
                </div>
              </v-sheet>
            </v-bottom-sheet>
          </div>
        </div>

        <div v-if="custom" class="custom" :class="{persistent}">
          <!-- @slot Default slot used to provide custom html for the toast -->
          <slot></slot>
        </div>
      </div>
    </v-sheet>
  </v-bottom-sheet>
</template>

<script>
import { VSheet, VBottomSheet, VDialog } from "vuetify/lib";
export default {
  name: "NsToast",
  data() {
    return {
      timeout: null,
      showToast: null,
      showCtaList: null
    };
  },
  components: {
    VBottomSheet,
    VSheet,
    VDialog
  },
  methods: {
    onClose() {
      this.showToast = false;
    },
    onCtaListClose() {
      this.showCtaList = false;
    },
    onCtaListOptionSelect(id, delay) {
      this.showCtaList = false;
      this.showToast = false;
      this.ctaClickHandler(id);
    },
    ctaClickHandler(id) {
      this.onCtaClick(id);
      this.showToast = false;
    }
  },
  props: {
    /**
     * Applies green color to the toast
     */
    success: { type: Boolean, default: false },
    /**
     * Applies red color to the toast
     */
    error: { type: Boolean, default: false },
    /**
     * Applies yellow color to the toast
     */
    info: { type: Boolean, default: false },
    /**
     * Applies yellow color to the toast
     */
    warning: { type: Boolean, default: false },
    /**
     * Adds icon that is relevant to a toast type (success, info, warning, error)
     */
    icon: { type: Boolean, default: false },
    /**
     * Clicking outside of the element will not deactivate it.
     */
    persistent: { type: Boolean, default: false },
    /**
     * Allows to use slot with custom html
     */
    custom: { type: Boolean, default: false },
    /**
     * Message inside toast
     */
    message: { type: String, default: "" },
    /**
     * Timout after which toast will deactivate
     */
    delay: { type: Number, default: 5000 },
    /**
     * Call to action link text
     */
    ctaLink: { type: String, default: "" },
    /**
     * Call to action button caption
     */
    ctaButton: { type: String, default: "" },
    /**
     * List of actions. Example: [{text: 'Primary CTA', id: 'unique-key'}, ...]
     */
    ctaList: {
      type: Array,
      default: () => {
        return [];
      }
    },
    /**
     * Triggers toast activation on value change (Hint: to make toast show do showTrigger = !showTrigger)
     */
    showTrigger: { type: Boolean | String, default: false },
    /**
     * Fires when any CTA is clicked. Passes id of a clicked cta
     */
    onCtaClick: { type: Function, default: () => {} }
  },

  watch: {
    showTrigger: function(val) {
      clearTimeout(this.timeout);
      this.showToast = true;
      if (this.persistent || this.ctaList.length) return;
      this.timeout = setTimeout(() => {
        this.showToast = false;
        clearTimeout(this.timeout);
      }, this.delay);
    },
    showCtaList: function(val) {
      if (val) {
        this.$refs.chevron.className += " active";
      } else {
        this.$refs.chevron.className = this.$refs.chevron.className
          .split(" active")
          .join("");
      }
    }
  }
};
</script>

<style scoped lang="scss">
$success-alert: rgb(92, 199, 105);
$success-icon: rgb(101, 208, 107);
$success-light-alert: rgb(233, 248, 239);
$warning-alert: rgb(246, 213, 73);
$warning-icon: rgb(245, 190, 66);
$warning-light-alert: rgb(253, 246, 209);
$info-alert: rgb(25, 66, 179);
$info-icon: rgb(90, 177, 234);
$info-light-alert: rgb(227, 242, 254);
$error-alert: rgb(162, 32, 21);
$error-icon: rgb(164, 40, 27);
$error-light-alert: rgb(244, 230, 229);

.cta-list-container {
  font-family: proxima-nova;
  height: 100%;
  // border-radius: 10px;
  display: flex;
  flex-direction: column;

  .actions {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    -webkit-clip-path: inset(0 0 0 0 round 10px);
    clip-path: inset(0 0 0 0 round 10px);
    a {
      padding: 15px;
      width: 100%;
      color: inherit;
      border-bottom: 1px solid #eee;
      font-size: 18px;
      &:last-child {
        border-bottom: none;
      }
    }
  }
  .header {
    position: relative;
    padding: 15px 18px;
    display: flex;
    flex-direction: column;
    align-content: center;
    justify-content: center;
    width: -webkit-fill-available;
    border-bottom: 1px solid #eee;
    h3 {
      font-weight: bolder;
      text-align: center;
      margin-bottom: 10px;
      font-size: 20px;
    }
    p {
      font-size: 18px;
      text-align: center;
    }
    .close {
      position: absolute;
      top: 12px;
      right: 15px;
      cursor: pointer;
    }
  }
}

.container {
  font-family: proxima-nova;
  padding: 15px 12px;
  font-size: 16px;
  line-height: 18px;
  background-color: white;
  border-top: 3px solid white;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  position: relative;

  .wrapper {
    width: 100%;
    max-width: 500px;
    position: relative;
    display: flex;
    align-items: center;

    .toast-icon {
      margin-right: 10px;
      font-size: 20px;
      align-self: center;
    }
    .close {
      margin: 0 8px 0 20px;
      position: absolute;
      right: 0;
      font-size: 24px;
      cursor: pointer;
    }
    .content {
      display: flex;
      min-width: 100%;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      align-items: center;
      &.align-left {
        justify-content: space-between;
      }
      &.persistent {
        padding-right: 60px;
      }
      .toast-simple {
        display: flex;
        flex-direction: row;
        align-items: center;
        line-height: 1.35em;
        padding-right: 40px;
      }
      .cta {
        position: relative;
        text-align: right;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        a {
          width: 100%;
          text-decoration: underline;
          color: #232121;
          font-weight: bold;
          font-size: 16px;
          white-space: nowrap;
        }
        button {
          white-space: nowrap;
        }
        &.list {
          a {
            text-transform: uppercase;
            text-decoration: none;
            line-height: 2em;
            margin-right: 5px;
            border-right: 2px solid rgb(35, 33, 33);
            padding-right: 15px;
          }
          i {
            cursor: pointer;
            font-size: 30px;
            transition: transform 0.2s ease-in;
            &.active {
              transform: rotate(180deg);
            }
          }
        }
      }
    }
    .custom {
      width: 100%;
      &.persistent {
        padding-right: 60px;
      }
    }
  }

  &.success {
    background-color: $success-light-alert !important;
    border-top-color: $success-alert !important;
    .toast-icon {
      color: $success-icon;
    }
  }
  &.error {
    background-color: $error-light-alert !important;
    border-top-color: $error-alert !important;
    .toast-icon {
      color: $error-icon;
    }
  }
  &.warning {
    background-color: $warning-light-alert !important;
    border-top-color: $warning-alert !important;
    .toast-icon {
      color: $warning-icon;
    }
  }
  &.info {
    background-color: $info-light-alert !important;
    border-top-color: $info-alert !important;
    .toast-icon {
      color: $info-icon;
    }
  }
}
</style>
